import {
  forwardRef,
  type ChangeEvent,
  type ChangeEventHandler,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';
import { T } from '@territory/tokens';
import { REMIX_FIELD_CSS, remixFieldStyle, type RemixFieldVariant } from '../internal/remixField.js';
import { font, radius, space, weight } from '../scale.js';
import { marketingDispatchFieldStyle } from '../internal/marketingDispatchField.js';
import { marketingResourceFieldStyle } from '../internal/marketingResourceField.js';
import { WEDGE_AUTH_FIELD_CSS, wedgeAuthFieldStyle, type WedgeAuthFieldVariant } from '../internal/wedgeAuthField.js';
import { RECON_HOVER_MS } from '../internal/reconRoles.js';
import { useStyleOnce } from '../internal/useStyleOnce.js';
import {
  ADORN,
  ADORN_PAD,
  FIELD_CONSOLE_CSS,
  FIELD_CSS,
  FIELD_INLINE_CSS,
  FieldLabel,
  FieldMessage,
  consoleControlStyle,
  consoleInlineControlStyle,
  panelControlStyle,
  controlStyle,
  describedBy,
  renderIconSlot,
  rootStyle,
  useFieldIds,
  type FieldVariant,
  type IconSlot,
} from './field.js';

interface TextFieldBaseProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'style' | 'size' | 'onChange' | 'value' | 'defaultValue'> {
  /* Visible label; use hideLabel for search boxes. */
  label?: ReactNode;
  hideLabel?: boolean;
  hint?: ReactNode;
  /* Sets aria-invalid and replaces the hint. */
  error?: ReactNode;
  required?: boolean;
  value?: string;
  defaultValue?: string;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onValueChange?: (value: string) => void;
  /* Renders a textarea. */
  multiline?: boolean;
  rows?: number;
  size?: 'sm' | 'md';
  leading?: IconSlot;
  /* Clear button, valid check, unit. */
  trailing?: ReactNode;
  /* T.mono text, the consoles terminal look. */
  mono?: boolean;
  /* 'console': the owner console field (legacy .finput): 36px, mono 13px, console field roles. `size` is ignored, except
     size="sm" draws the 32px journal field.
     'console-inline': the owner Enterprise inline field (legacy `IS`); `size` and `mono` are ignored.
     'recon': the shipped Recon .text-input, a bare 54px input with no wrapper, label, hint or message of its own (the
     app lays out the visible label). `multiline` renders the shipped day-note textarea. `label` names the input (aria-label) unless aria-labelledby is set; `className`,
     `style` and the ref land on the input; `error` sets aria-invalid, painted amber; `leading`, `trailing`,
     `size` and `mono` are ignored. The look is a first-injected sheet at the shipped specificity. */
  variant?: FieldVariant | 'recon' | WedgeAuthFieldVariant | 'marketing-resource' | 'marketing-dispatch' | RemixFieldVariant;
  /* Marketing dispatch retains caller focus handlers, labels and messages. */
  dispatchFocused?: boolean;
  /* Exact brandField visual state. The caller retains native focus and blur handlers. Ignored by other variants. */
  authFocused?: boolean;
  /* Native auth only. Touch adds the released Reset minimum height; other geometry stays unchanged. */
  authSize?: 'touch' | 'recovery';
  /* Shared auth only. PIN callers retain their flat focus paint without the brandField shadow. */
  authFocus?: 'flat';
  /* Shared auth only. Grant fields retain the browser's initial transition values. */
  authMotion?: 'instant';
  /* Account only. Retains the caller's native password visibility inset. */
  authInset?: 'password';
  /* Landing account/magic only. Visual border state; sets no native or ARIA validation state. */
  authBorder?: 'warning';
  className?: string;
  style?: CSSProperties;
  'data-testid'?: string;
}

/* Existing variants require a label. Native auth and Marketing resource inputs retain caller labels or ARIA. */
export type TextFieldProps = TextFieldBaseProps & (
  | { variant: WedgeAuthFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: 'marketing-resource'; multiline?: false; label?: ReactNode }
  | { variant: 'marketing-dispatch'; label?: ReactNode }
  | { variant: RemixFieldVariant; multiline?: false; label?: ReactNode }
  | { variant?: FieldVariant | 'recon'; label: ReactNode }
);

/* The ref is the input (or textarea), which is what forms focus and read; style and className land on the root. */
/* Recon .text-input, one for one, at class specificity (it must beat the global `input { font: inherit }`). The sheet
   goes in first, so Recon layout classes (.filters .text-input, .stepper input) on the same element still win. */
const RC = '[data-rv-recon-field]';
const RECON_FIELD_CSS = `${RC} { width: 100%; min-height: 54px; padding: 0 ${space[4]}px; border-radius: ${radius.lg}px; border: 1px solid ${T.separatorBold}; background: ${T.surface}; font-size: ${font.title3}px; font-weight: ${weight.medium}; transition: border-color ${RECON_HOVER_MS}ms ${T.ease}, box-shadow ${RECON_HOVER_MS}ms ${T.ease}; }
:where([data-rv-recon-field])[data-rv-multiline] { display: block; min-height: 96px; padding: ${space[3]}px ${space[4]}px; resize: vertical; line-height: 1.4; }
${RC}::placeholder { color: ${T.labelTer}; font-weight: ${weight.regular}; }
${RC}:focus { outline: none; border-color: ${T.accent}; box-shadow: 0 0 0 4px ${T.accentSoft}; }
${RC}[aria-invalid="true"] { border-color: ${T.amber}; box-shadow: 0 0 0 4px ${T.amberSoft}; }`;

export const TextField = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextFieldProps>(function TextField(props, ref) {
  const {
    label,
    hideLabel,
    hint,
    error,
    required,
    onChange,
    onValueChange,
    multiline,
    rows = 3,
    size = 'md',
    leading,
    trailing,
    mono,
    variant = 'default',
    dispatchFocused = false,
    authFocused = false,
    authSize,
    authFocus,
    authMotion,
    authInset,
    authBorder,
    id,
    disabled,
    className,
    style,
    'data-testid': testId,
    'aria-describedby': ownDescribedBy,
    ...rest
  } = props;
  useStyleOnce('remix-field', REMIX_FIELD_CSS);
  useStyleOnce('field', FIELD_CSS);
  useStyleOnce('field-console', FIELD_CONSOLE_CSS);
  useStyleOnce('field-inline', FIELD_INLINE_CSS);
  useStyleOnce('field-recon', RECON_FIELD_CSS, { first: true });
  useStyleOnce('field-wedge-auth', WEDGE_AUTH_FIELD_CSS);
  const ids = useFieldIds(id);
  const panelLook = variant === 'panel';
  const consoleLook = variant === 'console' || panelLook;
  const compact = variant === 'console' && size === 'sm';
  const inlineLook = variant === 'console-inline';
  const invalid = Boolean(error);
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange?.(e);
    onValueChange?.(e.target.value);
  };
  if (variant === 'wedge-auth' || variant === 'wedge-auth-recipient' || variant === 'wedge-auth-pin' || variant === 'wedge-auth-account' || variant === 'wedge-auth-magic') {
    return (
      <input
        {...rest}
        ref={ref as Ref<HTMLInputElement>}
        id={id}
        data-rv-auth-field={variant}
        data-testid={testId}
        className={className}
        style={{ ...wedgeAuthFieldStyle(variant, authFocused, {size: authSize, focus: authFocus, motion: authMotion, inset: authInset, border: authBorder}), ...style }}
        disabled={disabled}
        required={required}
        aria-describedby={ownDescribedBy}
        onChange={handleChange}
      />
    );
  }
  if (variant === 'remix' || variant === 'remix-editor' || variant === 'remix-url' || variant === 'remix-number') {
    return <input ref={ref as Ref<HTMLInputElement>} {...rest} id={id} disabled={disabled} required={required}
      data-rv-remix-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...remixFieldStyle(variant), ...style }} />;
  }
  if (variant === 'marketing-resource') {
    return <input ref={ref as Ref<HTMLInputElement>} {...rest} id={id} disabled={disabled} required={required}
      data-rv-marketing-field="resource" data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...marketingResourceFieldStyle(), ...style }} />;
  }
  if (variant === 'marketing-dispatch') {
    const shared = {
      id, disabled, required, className, 'data-testid': testId,
      'data-rv-marketing-field': 'dispatch',
      'aria-describedby': ownDescribedBy,
      onChange: handleChange,
      /* The shipped wrapper lets a caller replace the entire style object. */
      style: Object.prototype.hasOwnProperty.call(props, 'style') ? style : marketingDispatchFieldStyle(dispatchFocused, invalid, Boolean(multiline), rows),
    };
    return multiline
      ? <textarea ref={ref as Ref<HTMLTextAreaElement>} rows={rows} {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} {...shared} />
      : <input ref={ref as Ref<HTMLInputElement>} {...rest} {...shared} />;
  }
  if (variant === 'recon') {
    const named = rest['aria-labelledby'] === undefined && typeof label === 'string' ? label : undefined;
    const sharedRecon = {
      ...rest,
      id,
      'data-rv-recon-field': '',
      'data-testid': testId,
      className,
      style,
      disabled,
      required,
      'aria-label': named,
      'aria-invalid': invalid || undefined,
      'aria-describedby': ownDescribedBy,
      onChange: handleChange,
    } as const;
    return multiline ? (
      <textarea {...(sharedRecon as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} data-rv-multiline="" rows={rows} />
    ) : (
      <input type="text" {...sharedRecon} ref={ref as Ref<HTMLInputElement>} />
    );
  }
  const lead = renderIconSlot(leading, ADORN);
  const base = inlineLook
    ? consoleInlineControlStyle({ error: invalid, multiline })
    : panelLook
    ? panelControlStyle({ error: invalid, multiline })
    : consoleLook
    ? consoleControlStyle({ error: invalid, multiline, compact })
    : controlStyle({ size, error: invalid, multiline, mono });
  const inputStyle: CSSProperties = {
    ...base,
    /* Spread only when set: an undefined longhand after the padding shorthand clears it in React. */
    ...(lead ? { paddingLeft: ADORN_PAD } : null),
    ...(trailing ? { paddingRight: space[10] } : null),
  };
  const shared = {
    id: ids.id,
    'data-rv-field-control': '',
    'data-rv-look': inlineLook ? 'console-inline' : consoleLook ? 'console' : undefined,
    disabled,
    required,
    'aria-required': required || undefined,
    'aria-invalid': invalid || undefined,
    'aria-describedby': describedBy(ids, ownDescribedBy, hint, error),
    onChange: handleChange,
    style: inputStyle,
  };
  const adornBox: CSSProperties = {
    position: 'absolute',
    top: multiline ? space[3] : 0,
    bottom: multiline ? undefined : 0,
    display: 'flex',
    alignItems: 'center',
    color: T.labelTer,
  };

  return (
    <div data-rv-textfield="" data-testid={testId} className={className} style={rootStyle(disabled, style)}>
      <FieldLabel htmlFor={ids.id} hidden={hideLabel} required={required} caps={panelLook}>
        {label}
      </FieldLabel>
      <div style={{ position: 'relative' }}>
        {multiline ? (
          <textarea
            ref={ref as Ref<HTMLTextAreaElement>}
            rows={rows}
            {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)}
            {...shared}
          />
        ) : (
          <input ref={ref as Ref<HTMLInputElement>} type="text" {...rest} {...shared} />
        )}
        {lead ? (
          <span aria-hidden="true" style={{ ...adornBox, left: space[3], pointerEvents: 'none' }}>
            {lead}
          </span>
        ) : null}
        {trailing ? <span style={{ ...adornBox, right: space[3] }}>{trailing}</span> : null}
      </div>
      <FieldMessage ids={ids} hint={hint} error={error} caps={panelLook} />
    </div>
  );
});
